import {
  AcknowledgementName,
  AcknowledgementStatus,
  Configuration,
  PersonInviteRedemption,
  PersonsApi,
  typedErrorMiddleware
} from "@wingspanhq/wingspan-api-sdk";
import { acknowledgementsApi, personsApi } from "@api";
import { config } from "@config";
import { sessionService } from "@services/sessionService";
import { useWSMutation, WSQueryCache } from "@ws-react-query";
import { useCaptcha } from "../../../components/Captcha";
import { QUERY_USERS_SESSION } from "../../../query/session";
import { useMutationCreateSession } from "../../../query/users/mutations";
import { acknowledgementVersions } from "../../../shared/constants/acknowledgements";

const SIGN_UP_CONSENTS: AcknowledgementName[] = [
  AcknowledgementName.WINGSPAN_TOS_ACCEPTANCE,
  AcknowledgementName.WINGSPAN_PRIVACY_POLICY_ACCEPTANCE,
  AcknowledgementName.ELECTRONIC_DISCLOSURE_AND_CONSENT
];

export type CompleteRegistrationData = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  remember: boolean;
};

export const useMutationCompleteRegistration = (
  redemption: PersonInviteRedemption
) => {
  const [createV1Session] = useMutationCreateSession();
  const callWithCaptcha = useCaptcha("createSession");

  return useWSMutation<void, unknown, CompleteRegistrationData>(
    async data => {
      // The redemption token is the credential here, so the shared `apiConfig`
      // (whose refresh middleware would swap in the session bearer) does not
      // apply. The typed-error middleware still does: a rejected redemption
      // must surface as a ProblemDetailError the form's ErrorAlert can read.
      const redemptionPersonsApi = new PersonsApi(
        new Configuration({
          basePath: config.apiBaseUrl,
          accessToken: () => redemption.sessionToken,
          middleware: [typedErrorMiddleware]
        })
      );
      await redemptionPersonsApi.resetPersonPassword({
        personId: redemption.personId,
        resetPersonPasswordRequest: {
          newPassword: data.password
        }
      });

      await callWithCaptcha((captchaToken, captchaVersion, forceCaptcha) =>
        sessionService.login(data.email, data.password, data.remember, {
          captchaToken,
          captchaVersion,
          forceCaptcha
        })
      );

      await personsApi.submitPersonAsSelf({
        personSelfSubmission: {
          person: {
            firstName: data.firstName,
            lastName: data.lastName
          }
        }
      });

      await Promise.all(
        SIGN_UP_CONSENTS.map(async acknowledgementName => {
          const acknowledgement = await acknowledgementsApi.getAcknowledgement({
            acknowledgementName
          });
          if (
            acknowledgement.acknowledgementStatus !==
            AcknowledgementStatus.GIVEN
          ) {
            await acknowledgementsApi.acceptAcknowledgement({
              acknowledgementName,
              acceptAcknowledgementRequest: {
                version: acknowledgementVersions[acknowledgementName]
              }
            });
          }
        })
      );

      const session = await createV1Session(
        { email: data.email, password: data.password, remember: data.remember },
        { throwOnError: true }
      );
      if (session) {
        WSQueryCache.setQueryData(QUERY_USERS_SESSION, session);
      }
    },
    { dependencies: [QUERY_USERS_SESSION] }
  );
};
